import { withProjectCardStyle } from 'root/src/client/web/list/listItemComponents/style'
import { useDareCard } from 'root/src/client/web/list/listItemComponents/dare/hooks/useDareCard'
import { useBlackCardContext } from 'root/src/client/v2/common/components/BlackCard/hooks/useBlackCardContext'
import TooltipWrapper from 'root/src/client/web/base/TooltipWraper'
import Link from 'root/src/client/web/base/Link'
import { CONNECTED_CHANNELS_ROUTE_ID } from 'root/src/shared/descriptions/routes/routeIds'
import LoadingButtonNew from 'root/src/client/web/base/LoadingButtonNew'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import classNames from 'classnames'
import { addHttpsProtocol } from 'root/src/shared/util/protocolUtil'
import {
	ProjectCardLabel,
	statusColorsMap,
	statusLabels,
} from 'root/src/client/web/list/listItemComponents/project/ProjectCardLabel'
import { ProjectTimeLeft } from 'root/src/client/web/list/listItemComponents/project/ProjectCardHeader'
import { useClipboard } from 'root/src/client/web/hooks/useClipboard'
import ClipboardIcon from 'root/src/client/public/icons/ic-clipboard.svg'
import {
	dareExpiredMessage,
	maxDaresReachedMessage,
	noMaxDaresMessage,
	notAbleToParticipateMessage,
} from 'root/src/shared/constants/dropEvent'
import { DarePlatform } from 'root/src/shared/@types/platforms'
import { sentryClientObjMessage } from 'root/src/client/util/sentry'
import { useDropEvent } from 'root/src/client/logic/dropEvent/hooks/useDropEvent'
import { useUserCreatorData } from 'root/src/client/v2/common/hooks/useUser'
import { pick } from 'remeda'
import { getReadablePlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform'
import { isPlatform } from 'root/src/shared/platforms/guards/platform.guards'
import { getMessageLockedByTier } from 'root/src/client/v2/battlePass/helpers/battlePassMessage.helpers'

const getButtonText = (projectAssignees, subscribed, comingSoon, canAccessCreateBrandProject) => {
	if (canAccessCreateBrandProject) {
		return 'CREATE DARE'
	}

	if (comingSoon) {
		return subscribed ? 'NOT AVAILABLE NOW' : 'JOIN DROP'
	}

	return projectAssignees ? 'PLEDGE' : 'ACCEPT DARE'
}

const ShareLinkOverlay = withProjectCardStyle(({ classes, shareUrl }) => {
	const { writeText } = useClipboard()
	return (
		<button
			onClick={() => writeText(shareUrl)}
			type="button"
			className={classes.shareDareButton}
		>
			<img src={ClipboardIcon.src} alt="Clipboard" />
			<p className={classes.shareDareLink}>
				Share Link
			</p>
		</button>
	)
})

// TODO we should probably merge display tooltip and tooltip message logic
function DareButtonTooltip({ classes }) {
	const {
		extraData: {
			comingSoon,
			user: {
				userId,
				isAbleToParticipate,
				isDarePlatformAvailable,
				isMaxDaresReached,
				maxDares,
				isLockedByTier,
			},
			dareTimeLeft,
			dareCardContext,
		},
		dare: {
			battlePass,
			platform: darePlatform,
		},
	} = useDareCard()
	const drop = useDropEvent()
	const creatorData = useUserCreatorData()

	if (comingSoon) {
		return null
	}

	if (typeof dareTimeLeft === 'number' && dareTimeLeft <= 0) {
		return		(
			<p className={classes.buttonTooltip}>
				{dareExpiredMessage}
			</p>
		)
	}

	if (!isAbleToParticipate) {
		sentryClientObjMessage('User not able to participate message', {
			userId,
			dareCardContext: pick(dareCardContext, ['isAbleToParticipate']),
			creatorData,
			drop: pick(drop, ['isAbleToParticipate']),
		})
		return (
			<p className={classes.buttonTooltip}>
				{notAbleToParticipateMessage}
			</p>
		)
	}

	if (!isDarePlatformAvailable) {
		const readablePlatform = getMissingReadablePlatforms(darePlatform)

		return (
			<p className={classes.buttonTooltip}>
				This Dare is only for {readablePlatform}<br />
				you can connect it <Link routeId={CONNECTED_CHANNELS_ROUTE_ID}>here</Link>
			</p>
		)
	}

	if (isLockedByTier) {
		return (
			<p className={classes.buttonTooltip}>
				{getMessageLockedByTier(battlePass?.tier || 1)}
			</p>
		)
	}

	if (isMaxDaresReached) {
		return (
			<p className={classes.buttonTooltip}>
				{maxDaresReachedMessage}
			</p>
		)
	}

	if (!maxDares) {
		return (
			<p className={classes.buttonTooltip}>
				{noMaxDaresMessage}
			</p>
		)
	}

	return null
}

const getMissingReadablePlatforms = (platform) => {
	if (isPlatform(platform)) {
		return getReadablePlatform(platform)
	}

	return `${getReadablePlatform(DarePlatform.Twitch)}, ${getReadablePlatform(DarePlatform.TikTok)}, ${getReadablePlatform(DarePlatform.YouTube)}`
}

export const DareCardHeader = withProjectCardStyle(({ classes }) => {
	const {
		isLoading,
		dare: {
			games,
			title,
			landingShortUrl,
			creatorDareExpirationDate,
		},
		actions: {
			onAccept,
			onEmailSubscribe,
		},
		extraData: {
			labels,
			user: {
				isDareButtonEnabled,
				isEligibleToCreateDare,
			},
			dareCardContext: {
				projectDeliveries,
				projectAssignees, scrollPosition,
				emailSubscription, comingSoon,
				canAccessCreateBrandProject, dumb,
			},
			isInteracted,
			creatorDareTimeLeft,
			dareTimeLeft,
		},
	} = useDareCard()
	const { isHovered } = useBlackCardContext()

	const onClick = () => {
		if (!isEligibleToCreateDare && comingSoon) {
			return onEmailSubscribe()
		}
		return onAccept()
	}

	return (
		<div className={classes.projectCardHeader}>
			{!dumb && isHovered && !isInteracted && (
				<div className={classes.ctaButtonOverlay}>
					<TooltipWrapper
						tooltipText={(
							!isDareButtonEnabled && <DareButtonTooltip classes={classes} />
						)}
					>
						<LoadingButtonNew
							disabled={!isDareButtonEnabled}
							loading={isLoading}
							onClick={onClick}
							type="secondary"
						>
							{getButtonText(
								projectAssignees,
								emailSubscription,
								comingSoon,
								canAccessCreateBrandProject,
							)}
						</LoadingButtonNew>
					</TooltipWrapper>
				</div>
			)}
			<LazyLoadImage
				src={addHttpsProtocol(projectDeliveries ? projectDeliveries.thumbnail : games[0].image)}
				alt={title}
				scrollPosition={scrollPosition}
				className={classNames({
					[classes.thumbnail]: true,
					[classes.image]: true,
					[classes.smOpacity]: true,
					[classes.tiktokThumbnail]: projectDeliveries?.platform === 'tiktok',
					[classes.scaledUp]: isHovered,
					[classes.scaledDown]: !isHovered,
				})}
			/>
			{isHovered && landingShortUrl && (
				<ShareLinkOverlay shareUrl={addHttpsProtocol(landingShortUrl)} />
			)}
			<div className={classes.badgesWrapper}>
				<div className={classes.badgesContainer}>
					<div className={classes.marksContainer}>
						{labels.map(label => (
							<ProjectCardLabel
								key={label}
								status={statusLabels[label]}
								color={statusColorsMap[label]}
							/>
						))}
					</div>
					{
						(typeof dareTimeLeft === 'number' || typeof creatorDareTimeLeft === 'number')
							&& (
								<ProjectTimeLeft
									timeLeft={creatorDareTimeLeft || dareTimeLeft}
									expirationDate={creatorDareExpirationDate}
								/>
							)
					}
				</div>
			</div>
		</div>
	)
})
